// src/services/tables/commissionerContractColumns.js - COMMISSIONER CONTRACT OVERRIDE COLUMNS
import React from 'react';
import { dynastyTheme } from '../colorService';
import { formatContractDisplay } from '../contractUtils';
import { parseFloatOrPrev, parseFloatOrDefault } from '../formUtils';
import PlayerLink from '../../components/PlayerLink';

const DEFAULT_EXTENSION_RULES = [
  { years: 1, cost_increase: 0, description: 'Same price, 1 year' },
  { years: 2, cost_increase: 10, description: '+$10, 2 years' },
  { years: 3, cost_increase: 20, description: '+$20, 3 years' },
  { years: 4, cost_increase: 30, description: '+$30, 4 years' },
  { years: 5, cost_increase: 40, description: '+$40, 5 years' }
];

/**
 * Build salary dropdown options: $0 + min_salary to salary_cap in salary_increment steps.
 * Defaults match backend Pydantic schema at routers/leagues/lifecycle.py:128-136
 * (min_salary=2, salary_cap=800, salary_increment=2). ?? preserves user-set 0 keystones.
 * Infinity guard: if step <= 0 (legitimate user-set 0 after eradication of `|| 2`
 * silent coercion), return [0] only — a $0-only dropdown is degenerate but safe.
 * Exported for unit-test coverage.
 */
export const buildSalaryOptions = (salarySettings) => {
  const min = salarySettings?.min_salary ?? 2;
  const max = salarySettings?.salary_cap ?? 800;
  const step = salarySettings?.salary_increment ?? 2;
  const options = [0];
  if (step > 0) {
    for (let v = min; v <= max; v += step) {
      options.push(v);
    }
  }
  return options;
};

/** Build contract type options from extension rules. */
const buildContractTypeOptions = (salarySettings) => {
  const rules = (salarySettings?.extension_rules?.length > 0)
    ? salarySettings.extension_rules
    : DEFAULT_EXTENSION_RULES;

  const options = [
    { value: 'NOT_SET', label: '\u26A0 Not Set \u2014 Must Fix' },
    { value: 'pending', label: 'Contract Pending (Keeper Decision Required)' },
    { value: 'none_minors', label: 'None \u2014 Minors (ML Draft Player)' },
    { value: '1', label: 'Standard 1-Year' },
    { value: '2', label: 'Standard 2-Year' }
  ];

  rules.forEach((rule, index) => {
    const type = index === 0 ? 'OP' : `OP${index}`;
    const costLabel = rule.cost_increase > 0 ? `+$${rule.cost_increase}` : '+$0';
    options.push({
      value: type,
      label: `${type} (${rule.years}yr, ${costLabel})`
    });
  });

  return options;
};

/**
 * Create editable columns for commissioner contract override.
 * Reads canonical player structure: ids.league_player, info.*, league_context.financial.*
 */
export const createCommissionerContractColumns = ({
  editingPlayer,
  editedContracts,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onContractChange,
  salarySettings
}) => {
  const salaryOptions = buildSalaryOptions(salarySettings);
  const contractTypeOptions = buildContractTypeOptions(salarySettings);

  return [
    // Player Name
    {
      key: 'info.full_name',
      title: 'Player',
      width: 180,
      cellPadding: 'py-2 px-3',
      textAlign: 'text-left',
      sortable: true,
      allowOverflow: true,
      render: (player) => {
        return (
          <div className="flex flex-col">
            <PlayerLink
              mlbId={player.ids?.mlb}
              name={player.info.full_name}
              style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }}
            />
            <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>
              {/* String `||` (not `??`): empty position string treated as missing for display. */}
              {player.info.position || 'N/A'}
            </span>
          </div>
        );
      }
    },

    // MLB Team
    {
      key: 'info.mlb_team',
      title: 'Team',
      width: 60,
      cellPadding: 'py-2 px-3',
      sortable: true,
      render: (player) => {
        return (
          <span className={`${dynastyTheme.classes.text.white} text-sm`}>
            {/* String `||` (not `??`): empty team string treated as missing for display. */}
            {player.info.mlb_team || '-'}
          </span>
        );
      }
    },

    // Price (Read-only)
    {
      key: 'league_context.financial.baseline_price',
      title: 'Price',
      width: 100,
      cellPadding: 'py-2 px-3',
      sortable: true,
      render: (player) => {
        const priceValue = parseFloatOrDefault(player.league_context.financial.baseline_price, 0);
        return (
          <span className={`${dynastyTheme.classes.text.warning} font-semibold text-sm`}>
            ${Math.round(priceValue)}
          </span>
        );
      }
    },

    // Salary - EDITABLE (dropdown)
    {
      key: 'league_context.financial.contract_salary',
      title: 'Salary',
      width: 120,
      cellPadding: 'py-2 px-3',
      sortable: true,
      render: (player) => {
        const lpId = player.ids.league_player;
        const isEditing = editingPlayer === lpId;
        // Always prefer editedContracts (pending edits) over canonical data
        const currentValue = editedContracts[lpId]?.salary ?? player.league_context.financial.contract_salary;

        if (isEditing) {
          return (
            <select
              value={currentValue}
              onChange={(e) => onContractChange(lpId, 'salary', parseFloatOrPrev(e.target.value, currentValue))}
              onClick={(e) => e.stopPropagation()}
              className={`w-full px-2 py-1 text-sm ${dynastyTheme.components.input} focus:ring-2 focus:ring-yellow-400`}
            >
              {salaryOptions.map(v => (
                <option key={v} value={v}>${v}</option>
              ))}
            </select>
          );
        }

        const salaryValue = parseFloatOrDefault(currentValue, 0);
        return (
          <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
            ${Math.round(salaryValue)}
          </span>
        );
      }
    },

    // Contract Type - EDITABLE (dropdown, replaces Years input)
    {
      key: 'league_context.financial.contract_type',
      title: 'Contract',
      width: 160,
      cellPadding: 'py-2 px-3',
      sortable: true,
      render: (player) => {
        const lpId = player.ids.league_player;
        const isEditing = editingPlayer === lpId;
        // Always prefer editedContracts (pending edits) over canonical data
        const currentType = editedContracts[lpId]?.contract_type ?? player.league_context.financial.contract_type;
        const currentYears = editedContracts[lpId]?.contract_years ?? player.league_context.financial.contract_years;

        if (isEditing) {
          // In edit mode, map null/0 to 'pending' for the dropdown
          // String `||` (not `??`): empty contract-type string treated as missing; '1' canonical default for dropdown value.
          const dropdownValue = (!currentType && currentYears === 0) ? 'pending' : (currentType || '1');
          return (
            <select
              value={dropdownValue}
              onChange={(e) => onContractChange(lpId, 'contract_type', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className={`w-full px-2 py-1 text-sm ${dynastyTheme.components.input} focus:ring-2 focus:ring-yellow-400`}
            >
              {contractTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        }

        // Minor leaguer: no contract display
        const rosterStatus = player.league_context?.roster?.status;
        if (rosterStatus === 'minors') {
          return (
            <span className="text-xs font-medium text-neutral-500">
              None — Not Activated from Minors
            </span>
          );
        }

        // Display mode: pending badge
        if (!currentType && currentYears === 0) {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-yellow-400/15 text-yellow-400 border border-yellow-400/30">
              PENDING — Keeper Decision
            </span>
          );
        }

        // Display mode: show human-readable label from contractTypeOptions
        const matchedOption = currentType
          ? contractTypeOptions.find(opt => opt.value === currentType)
          : null;
        // String `||` (not `??`): empty contract-type string treated as missing for label display.
        const typeLabel = matchedOption ? matchedOption.label : (currentType || 'Not Set');
        return (
          <span className={`${dynastyTheme.classes.text.white} font-semibold text-sm`}>
            {typeLabel} ({formatContractDisplay(currentType, currentYears)})
          </span>
        );
      }
    },

    // Status
    {
      key: 'contract_status',
      title: 'Status',
      width: 140,
      cellPadding: 'py-2 px-3',
      sortable: false,
      render: (player) => {
        const fin = player.league_context?.financial ?? {};
        const type = fin.contract_type;
        const years = fin.contract_years;
        const rosterStatus = player.league_context?.roster?.status;

        const acquisitionMethod = player.league_context?.acquisition?.method;

        // ML player — minor league draft, not activated
        if (rosterStatus === 'minors' || (acquisitionMethod === 'minor_league_draft' && !fin.salary && !years)) {
          return (
            <span className="text-xs font-medium text-neutral-500">
              {rosterStatus === 'minors' ? 'Minors' : 'ML — Not Activated'}
            </span>
          );
        }

        // Pending keeper decision (contract expired, awaiting extension or release)
        if (!type && years === 0) {
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-400/15 text-yellow-400 border border-yellow-400/30">
              PENDING
            </span>
          );
        }

        // Extended with more option years remaining (OP1, OP2, OP3+)
        if (type && type.startsWith('OP') && type !== 'OP') {
          return (
            <span className="text-xs font-medium text-green-400">
              Extended
            </span>
          );
        }

        // Final Year - Extended (base OP, last option year)
        if (type === 'OP') {
          return (
            <span className="text-xs font-medium text-amber-400">
              Final Year - Extended
            </span>
          );
        }

        // Multi-year initial contract with years remaining
        if (years >= 2) {
          return (
            <span className="text-xs font-medium text-green-400">
              Active
            </span>
          );
        }

        // Final Year - Initial (standard numbered contract, last year)
        return (
          <span className="text-xs font-medium text-amber-400">
            Final Year - Initial
          </span>
        );
      }
    },

    // Actions
    {
      key: 'actions',
      title: 'Actions',
      width: 120,
      cellPadding: 'py-2 px-3',
      sortable: false,
      render: (player) => {
        const lpId = player.ids.league_player;
        const isEditing = editingPlayer === lpId;

        if (isEditing) {
          return (
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSaveEdit(lpId);
                }}
                className={`px-3 py-1 text-xs font-medium rounded ${dynastyTheme.classes.bg.success}/20 ${dynastyTheme.classes.text.success} hover:${dynastyTheme.classes.bg.success}/30 ${dynastyTheme.classes.transitionFast}`}
              >
                Save
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelEdit();
                }}
                className={`px-3 py-1 text-xs font-medium rounded ${dynastyTheme.classes.bg.error}/20 ${dynastyTheme.classes.text.error} hover:${dynastyTheme.classes.bg.error}/30 ${dynastyTheme.classes.transitionFast}`}
              >
                Cancel
              </button>
            </div>
          );
        }

        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStartEdit(player);
            }}
            className={`px-3 py-1 text-xs font-medium rounded ${dynastyTheme.classes.bg.primary}/20 ${dynastyTheme.classes.text.primary} hover:${dynastyTheme.classes.bg.primary}/30 ${dynastyTheme.classes.transitionFast}`}
          >
            Edit
          </button>
        );
      }
    }
  ];
};
